/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';

import { formatMoney, useFlags, PlaceholderText } from 'sarsaparilla';

import {
    getAgeRangeDescription,
    getDiscountPriceText,
    getFeeText,
    getQuantityDiscountThresholdText,
} from '../utils';

import { feeType, pricingByGuestTypeIdType } from '../types';
import { NPS_AGENCY } from '../constants';

function isFeeSame(a, b) {
    if (a && b) {
        const isPerSame = a?.per === b?.per;
        const isPriceSame = a?.unit_price === b?.unit_price;
        const isUnitsSame = a?.units === b?.units;

        return isPerSame && isPriceSame && isUnitsSame;
    }
    return true;
}

function RateTablePlaceholder() {
    return (
        <div className="te-rate-table-wrap" role="img" aria-label="Loading rate table">
            <table className="te-rate-table">
                <tbody>
                    <tr>
                        <th>
                            <PlaceholderText width="3.8rem" />
                        </th>
                        <td className="te-price-col">
                            <PlaceholderText width="4.5rem" alignX="right" />
                        </td>
                    </tr>
                    <tr>
                        <th>
                            <PlaceholderText width="4rem" />
                        </th>
                        <td className="te-price-col">
                            <PlaceholderText width="4.5rem" alignX="right" />
                        </td>
                    </tr>
                    <tr>
                        <th>
                            <PlaceholderText width="12.5rem" />
                        </th>
                        <td className="te-price-col">
                            <PlaceholderText width="4.5rem" alignX="right" />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

export function RateTable({
    isLoading = false,
    guestTypes = [],
    tourName,
    pricingByGuestTypeId = {},
    applicationFee,
    reservationFee,
    cancellationFee,
    lateCancellationFee,
    quantityDiscountThreshold,
    cancellationWindowDescription,
    acceptsPasses,
}) {
    const agencyName = useSelector((state) => state.timedEntry.facility.agency_name);
    const { ticketsFeeLanguage } = useFlags();

    // Return placeholder if loading
    if (isLoading) {
        return <RateTablePlaceholder />;
    }

    // ... Otherwise, render normal
    const isLateFeeSame = isFeeSame(cancellationFee, lateCancellationFee);
    const quantityDiscountText = getQuantityDiscountThresholdText(
        quantityDiscountThreshold
    );

    //Means the tour is and AddOn Fee model tour
    const addOnFeeModelTour = reservationFee.unit_price > 0;
    const transactionTour = !!pricingByGuestTypeId.TRANSACTION;
    const transFeeType = ticketsFeeLanguage ? 'service' : 'reservation';

    function renderPriceRows() {
        const guestTypesWithSchool = [...guestTypes];

        guestTypesWithSchool.push({
            guest_type_id: 'TRANSACTION',
            type: 'School Group Ticket',
        });

        const filteredGuestTypes = guestTypesWithSchool.filter(
            ({ guest_type_id: id }) => {
                const isValid =
                    !isEmpty(pricingByGuestTypeId[id]) &&
                    pricingByGuestTypeId[id]?.guest_type_allowed_by_tour;

                return isValid;
            }
        );

        return filteredGuestTypes.map((guestType) => {
            const { guest_type_id: guestTypeId } = guestType;

            const ageRangeDescription = getAgeRangeDescription(guestType);
            const guestTypePricing = pricingByGuestTypeId[guestTypeId];

            const ticketCost = guestTypePricing?.ticket_cost || 0;
            let displayPrice = '';
            let shouldCheckTicketCost = false;

            if (guestTypePricing.guest_type_allowed_by_tour) {
                if (!guestTypePricing.ticket_required) {
                    displayPrice = 'No Ticket Required';
                } else if (!guestTypePricing.exists_in_fee_model) {
                    displayPrice = 'Ticket Data Unavailable';
                } else {
                    displayPrice = formatMoney(ticketCost);
                    shouldCheckTicketCost = true;
                }
            }

            const discountedPriceText = getDiscountPriceText(
                ticketCost,
                guestTypePricing.quantity_discount
            );

            const shouldShowPriceRow =
                NPS_AGENCY !== agencyName || ticketCost > 0 || !shouldCheckTicketCost;

            if (
                (guestType.type === 'School Group Ticket' && addOnFeeModelTour) ||
                !shouldShowPriceRow
            ) {
                return null;
            }

            return (
                <tr key={guestTypeId}>
                    <td>
                        {guestType.type}
                        {guestType.type === 'School Group Ticket' &&
                            !addOnFeeModelTour && <sup className="te-sup">†</sup>}
                        {ageRangeDescription && (
                            <span className="te-help-text">
                                {` (${ageRangeDescription})`}
                            </span>
                        )}
                    </td>
                    <td className="te-price-col">
                        {displayPrice}
                        {discountedPriceText && (
                            <span>
                                <span className="te-price-divider">/</span>
                                {discountedPriceText}
                                <sup className="te-sup">*</sup>
                            </span>
                        )}
                    </td>
                </tr>
            );
        });
    }

    function renderFeeRow(label, fee, comment) {
        const isPerTransaction = !!pricingByGuestTypeId?.TRANSACTION;
        if (fee?.unit_price) {
            return (
                <tr>
                    <td>
                        {label}
                        {isPerTransaction && addOnFeeModelTour && (
                            <sup className="te-sup">†</sup>
                        )}
                    </td>
                    <td>{getFeeText(fee, comment, isPerTransaction)}</td>
                </tr>
            );
        }

        return null;
    }

    return (
        <div className="te-rate-table-wrap">
            <table className="te-rate-table">
                <caption>
                    <span className="rec-sr-only">{tourName} Rates</span>
                </caption>
                <thead>
                    <tr>
                        <th scope="col">
                            <span className="rec-sr-only">Ticket/Fee Type</span>
                        </th>
                        <th scope="col">
                            <span className="rec-sr-only">Rates</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {renderPriceRows()}
                    {renderFeeRow('Application Fee', applicationFee, ', nonrefundable')}
                    {renderFeeRow(
                        ticketsFeeLanguage ? 'Service Fee' : 'Reservation Fee',
                        reservationFee,
                        ', nonrefundable'
                    )}
                    {renderFeeRow('Cancellation Fee', cancellationFee, '')}
                    {!isLateFeeSame &&
                        renderFeeRow(
                            'Late Cancellation Fee',
                            lateCancellationFee,
                            cancellationWindowDescription
                        )}
                </tbody>
            </table>
            {quantityDiscountText && (
                <div className="te-rate-table-footnote">{quantityDiscountText}</div>
            )}
            {transactionTour && !addOnFeeModelTour && (
                <div className="te-rate-table-footnote">
                    † Customers purchasing a School Group Ticket must enter the number of
                    students and chaperones attending the tour. Each group will be charged
                    a flat fee.
                </div>
            )}
            {transactionTour && addOnFeeModelTour && (
                <div className="te-rate-table-footnote">
                    † Customers may purchase up to 4 tickets to enter the shuttle. Each
                    reservation will be charged a flat reservation fee of{' '}
                    {formatMoney(reservationFee.unit_price)}.
                </div>
            )}
            {acceptsPasses && addOnFeeModelTour && (
                <div className="te-rate-table-footnote">
                    † Pass holders are required to pay the non-refundable {transFeeType}{' '}
                    fee only. Add your pass on the Order Details page as you checkout
                </div>
            )}
        </div>
    );
}

RateTable.propTypes = {
    isLoading: PropTypes.bool,
    guestTypes: PropTypes.arrayOf(PropTypes.object),
    tourName: PropTypes.string,
    pricingByGuestTypeId: pricingByGuestTypeIdType,
    applicationFee: feeType,
    reservationFee: feeType,
    cancellationFee: feeType,
    lateCancellationFee: feeType,
    quantityDiscountThreshold: PropTypes.number,
    cancellationWindowDescription: PropTypes.string,
    acceptsPasses: PropTypes.bool,
};
